import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, FileText, X, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface ResumeUploadScreenProps {
  onResumeData: (data: Record<string, string[]>) => void;
  existingSelections: string[];
}

const ResumeUploadScreen = ({ onResumeData, existingSelections }: ResumeUploadScreenProps) => {
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<'idle' | 'uploading' | 'parsing' | 'done' | 'error'>('idle');
  const [parsedSkills, setParsedSkills] = useState<string[]>([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const handleFile = useCallback(async (selectedFile: File) => {
    if (!selectedFile) return;
    
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'text/plain',
    ];
    if (!validTypes.includes(selectedFile.type)) {
      setErrorMsg('Please upload a PDF, DOCX, DOC, or TXT file.');
      setStatus('error');
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      setErrorMsg('File must be under 10MB.');
      setStatus('error');
      return;
    }

    setFile(selectedFile);
    setStatus('uploading');
    setErrorMsg('');

    try {
      // Upload to storage if authenticated
      let fileUrl = '';
      if (user) {
        const filePath = `${user.id}/${Date.now()}-${selectedFile.name}`;
        const { error: uploadError } = await supabase.storage
          .from('resumes')
          .upload(filePath, selectedFile);
        if (uploadError) throw uploadError;
        fileUrl = filePath;
      }

      setStatus('parsing');

      // Read file content as text for parsing
      const text = await selectedFile.text();

      // Call edge function to parse resume
      const { data, error } = await supabase.functions.invoke('parse-resume', {
        body: { resumeText: text.substring(0, 15000), fileUrl },
      });

      if (error) throw error;

      const skills = data?.skills || [];
      const experience = data?.experience || [];
      const education = data?.education || [];

      setParsedSkills(skills);
      setStatus('done');

      // Pass extracted data back to auto-fill assessment
      const autoFillData: Record<string, string[]> = {};
      if (skills.length > 0) autoFillData.skills = skills;
      if (experience.length > 0) autoFillData.experience = experience;
      if (education.length > 0) autoFillData.education = education;
      onResumeData(autoFillData);
    } catch (err: any) {
      console.error('Resume parse error:', err);
      setErrorMsg(err.message || 'Failed to process resume. You can still continue without it.');
      setStatus('error');
    }
  }, [user, onResumeData]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFile(droppedFile);
  }, [handleFile]);

  const removeFile = () => {
    setFile(null);
    setStatus('idle');
    setParsedSkills([]);
    setErrorMsg('');
  };

  return (
    <div className="space-y-4">
      <AnimatePresence mode="wait">
        {status === 'idle' || status === 'error' ? (
          <motion.div
            key="upload"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
          >
            <label
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`flex flex-col items-center justify-center p-8 rounded-2xl border-2 border-dashed cursor-pointer transition-all ${
                dragOver
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:border-primary/50 bg-card/50'
              }`}
            >
              <Upload className={`w-10 h-10 mb-3 ${dragOver ? 'text-primary' : 'text-muted-foreground'}`} />
              <p className="text-sm font-medium text-foreground">Drop your resume here</p>
              <p className="text-xs text-muted-foreground mt-1">or click to browse · PDF, DOCX, TXT (max 10MB)</p>
              <input
                type="file"
                className="hidden"
                accept=".pdf,.docx,.doc,.txt"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                }}
              />
            </label>
            {status === 'error' && (
              <div className="mt-3 flex items-start gap-2 p-3 rounded-xl bg-destructive/10 border border-destructive/20">
                <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                <p className="text-xs text-destructive">{errorMsg}</p>
              </div>
            )}
          </motion.div>
        ) : status === 'uploading' || status === 'parsing' ? (
          <motion.div
            key="processing"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center p-8 rounded-2xl bg-card border border-border"
          >
            <Loader2 className="w-8 h-8 text-primary animate-spin mb-3" />
            <p className="text-sm font-medium text-foreground">
              {status === 'uploading' ? 'Uploading...' : 'Extracting skills & experience...'}
            </p>
            {file && (
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <FileText className="w-3 h-3" /> {file.name}
              </p>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="done"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-4"
          >
            <div className="flex items-center gap-3 p-4 rounded-xl bg-primary/10 border border-primary/20">
              <CheckCircle className="w-5 h-5 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">Resume processed</p>
                <p className="text-xs text-muted-foreground truncate">{file?.name}</p>
              </div>
              <button onClick={removeFile} className="text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            </div>

            {parsedSkills.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-foreground">Extracted skills & keywords:</p>
                <div className="flex flex-wrap gap-1.5">
                  {parsedSkills.map((skill) => (
                    <span key={skill} className="px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <p className="text-center text-xs text-muted-foreground">
        This step is optional — skip it if you prefer to fill in details manually.
      </p>
    </div>
  );
};

export default ResumeUploadScreen;
